import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bot, Download, PenLine, FolderTree, History, Link2, Loader2, MessageSquare, RotateCcw, Share2, Sparkles, Copy, Trash2 } from "lucide-react";
import { api, CATEGORY_LABELS, errMsg, fileKind, fmtBytes, fmtDate, fmtMoney, publicLinkUrl } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { FileIcon } from "@/components/files/FileIcon";
import { FilePreview, downloadFile } from "@/components/files/FilePreview";
import { APP_NAMES, NewVersionButton, OfficeEditDialog } from "@/components/files/OfficeEditDialog";
import { CompanySuggestion } from "@/components/files/CompanySuggestion";
import { PermissionsDialog } from "@/components/files/PermissionsDialog";
import { ApprovalTab } from "@/components/files/Approvals";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Field = ({ label, children }) => (
  <div className="py-2 grid grid-cols-[140px_1fr] gap-3 text-sm border-b last:border-0">
    <span className="text-slate-500">{label}</span><span className="font-medium break-words">{children ?? "—"}</span>
  </div>
);

function InfoTab({ file, reload, onChanged }) {
  const navigate = useNavigate();
  const { me } = useMe();
  const [busy, setBusy] = useState("");
  const ai = file.ai || {};
  const canEdit = ["edit", "owner"].includes(file.access);
  const call = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); toast.success(typeof ok === "function" ? ok(r.data) : ok); reload(); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-5">
      <div className="bg-white border rounded-lg p-4">
        <Field label="Emplacement">{file.breadcrumbs?.map((b) => b.name).join(" / ") || "Mes fichiers"}</Field>
        <Field label="Propriétaire">{file.owner_name}</Field>
        <Field label="Taille">{fmtBytes(file.size)}</Field>
        <Field label="Version">v{file.version}</Field>
        <Field label="Modifié">{fmtDate(file.updated_at, true)} {file.updated_by_name && `par ${file.updated_by_name}`}</Field>
        <Field label="Catégorie">
          {canEdit ? (
            <Select value={file.category || "autre"} onValueChange={(v) => call("cat", () => api.patch(`/files/${file.id}`, { category: v }), "Catégorie mise à jour")}>
              <SelectTrigger className="h-8 w-48" data-testid="file-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          ) : CATEGORY_LABELS[file.category]}
        </Field>
        <Field label="Date d'expiration">
          {canEdit ? (
            <Input type="date" className="h-8 w-48" defaultValue={file.expiry_date || ai.expiry_date || ""} data-testid="file-expiry-input"
              onBlur={(e) => e.target.value !== (file.expiry_date || "") && call("exp", () => api.patch(`/files/${file.id}`, { expiry_date: e.target.value }), "Échéance enregistrée")} />
          ) : (file.expiry_date || ai.expiry_date)}
        </Field>
      </div>

      <div className="rounded-lg border border-blue-100 bg-gradient-to-b from-blue-50/70 to-white p-4" data-testid="file-ai-panel">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h4 className="font-semibold text-sm flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#1E3A8A]" />Analyse IA</h4>
          <div className="flex gap-2 flex-wrap">
            {canEdit && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => call("an", () => api.post(`/ai/files/${file.id}/analyze`), "Analyse terminée")} data-testid="file-analyze-btn">
              {busy === "an" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Analyser"}</Button>}
            {canEdit && me.role !== "externe" && file.ai && <Button size="sm" variant="outline" disabled={!!busy} data-testid="file-autoclassify-btn"
              onClick={() => call("cl", () => api.post(`/ai/files/${file.id}/auto-classify`), (d) => `Classé dans ${d.path}`)}>
              <FolderTree className="h-4 w-4 mr-1.5" />Classer automatiquement</Button>}
            <Button size="sm" onClick={() => navigate(`/assistant?file=${file.id}`)} data-testid="file-ask-ai-btn"><Bot className="h-4 w-4 mr-1.5" />Interroger</Button>
          </div>
        </div>
        {file.ai_status === "pending" && <p className="text-sm text-slate-500 mt-3 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Analyse en cours…</p>}
        {file.ai_status === "no_text" && <p className="text-sm text-slate-500 mt-3">Aucun texte lisible dans ce fichier (vidéo, audio ou image sans texte).</p>}
        {file.ai_status === "error" && <p className="text-sm text-red-600 mt-3">L'analyse a échoué. Relancez-la.</p>}
        {file.ai && (
          <div className="mt-3 space-y-3">
            <p className="text-sm leading-relaxed text-slate-700" data-testid="file-ai-summary">{ai.summary}</p>
            <div className="bg-white border rounded-md px-3">
              <Field label="Type">{CATEGORY_LABELS[ai.doc_type]}</Field>
              <Field label="Fournisseur / émetteur">{ai.supplier}</Field>
              <Field label="Numéro">{ai.document_number}</Field>
              <Field label="Date">{ai.date}</Field>
              <Field label="Montant HT">{ai.amount_ht != null ? fmtMoney(ai.amount_ht, ai.currency) : null}</Field>
              <Field label="Montant TTC">{ai.amount_ttc != null ? fmtMoney(ai.amount_ttc, ai.currency) : null}</Field>
              <Field label="Échéance">{ai.expiry_date}</Field>
            </div>
            {ai.key_points?.length > 0 && (
              <ul className="text-sm list-disc pl-5 space-y-1 text-slate-700">{ai.key_points.map((k, i) => <li key={i}>{k}</li>)}</ul>
            )}
            {ai.ocr && file.text_preview && (
              <details className="bg-white border rounded-md p-3" data-testid="file-ocr-text">
                <summary className="text-sm font-semibold cursor-pointer">Texte reconnu sur le scan</summary>
                <pre className="whitespace-pre-wrap text-xs mt-2 font-mono max-h-64 overflow-auto">{file.text_preview}</pre>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function VersionsTab({ file, reload, onChanged }) {
  const canEdit = ["edit", "owner"].includes(file.access);
  const restore = async (v) => {
    try { await api.post(`/files/${file.id}/versions/${v}/restore`); toast.success(`Version ${v} restaurée`); reload(); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border rounded-lg divide-y" data-testid="versions-list">
      {[...(file.versions || [])].reverse().map((v) => (
        <div key={v.version} className="flex items-center gap-3 px-4 py-3" data-testid={`version-row-${v.version}`}>
          <span className={`font-mono text-xs px-2 py-1 rounded ${v.version === file.version ? "bg-[#1E3A8A] text-white" : "bg-slate-100"}`}>v{v.version}</span>
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-medium">{v.uploaded_by_name || "—"}{v.restored_from && <span className="text-slate-500 font-normal"> · restaurée depuis v{v.restored_from}</span>}</div>
            <div className="text-xs text-slate-500">{fmtDate(v.uploaded_at, true)} · {fmtBytes(v.size)}</div>
          </div>
          <Button variant="ghost" size="icon" aria-label="Télécharger" onClick={() => downloadFile(file, v.version)} data-testid={`version-download-${v.version}`}><Download className="h-4 w-4" /></Button>
          {canEdit && v.version !== file.version && (
            <Button variant="outline" size="sm" onClick={() => restore(v.version)} data-testid={`version-restore-${v.version}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Restaurer</Button>
          )}
        </div>
      ))}
      <div className="px-4 py-3 flex flex-wrap items-center gap-3">
        {canEdit && <NewVersionButton file={file} onDone={() => { reload(); onChanged(); }} />}
        <p className="text-xs text-slate-500 flex-1">Vous pouvez aussi téléverser un fichier portant le même nom dans le même dossier.</p>
      </div>
    </div>
  );
}

function CommentsTab({ file }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const load = () => api.get(`/files/${file.id}/comments`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [file.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    if (!text.trim()) return;
    try { await api.post(`/files/${file.id}/comments`, { text }); setText(""); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3">
      <div className="space-y-2" data-testid="comments-list">
        {items.length === 0 && <p className="text-sm text-slate-500">Aucun commentaire pour l'instant.</p>}
        {items.map((c) => (
          <div key={c.id} className="bg-white border rounded-lg p-3">
            <div className="text-xs text-slate-500"><span className="font-semibold text-slate-800">{c.user_name}</span> · {fmtDate(c.created_at, true)}</div>
            <p className="text-sm mt-1 whitespace-pre-wrap">{c.text}</p>
          </div>
        ))}
      </div>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Ajouter un commentaire…" data-testid="comment-input" />
      <Button onClick={send} disabled={!text.trim()} data-testid="comment-submit-btn"><MessageSquare className="h-4 w-4 mr-2" />Publier</Button>
    </div>
  );
}

function ShareTab({ file, reload, onChanged }) {
  const { me } = useMe();
  const [links, setLinks] = useState([]);
  const [days, setDays] = useState("7");
  const [password, setPassword] = useState("");
  const [allowDl, setAllowDl] = useState(true);
  const [permOpen, setPermOpen] = useState(false);
  const load = () => api.get("/links", { params: { file_id: file.id } }).then((r) => setLinks(r.data));
  useEffect(() => { load(); }, [file.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const create = async () => {
    try {
      const { data } = await api.post(`/files/${file.id}/links`, { expires_in_days: Number(days), password: password || null, allow_download: allowDl });
      await navigator.clipboard?.writeText(publicLinkUrl(data.token)).catch(() => {});
      toast.success("Lien créé et copié"); setPassword(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async (id) => { await api.delete(`/links/${id}`); toast.success("Lien révoqué"); load(); };
  const canLink = me.role !== "externe" && ["edit", "owner"].includes(file.access);
  return (
    <div className="space-y-5">
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-sm">Collaborateurs</h4>
            <p className="text-xs text-slate-500">{file.permissions?.length ? `${file.permissions.length} autorisation(s) sur ce fichier` : "Privé (hors autorisations du dossier)"}</p>
          </div>
          {file.access === "owner" && me.role !== "externe" && <Button size="sm" variant="outline" onClick={() => setPermOpen(true)} data-testid="file-permissions-btn"><Share2 className="h-4 w-4 mr-1.5" />Gérer</Button>}
        </div>
      </div>
      {canLink && (
        <div className="bg-white border rounded-lg p-4 space-y-3">
          <h4 className="font-semibold text-sm flex items-center gap-2"><Link2 className="h-4 w-4" />Lien de partage sécurisé</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Expiration</Label>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger data-testid="link-expiry-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[["1", "24 heures"], ["7", "7 jours"], ["30", "30 jours"], ["90", "90 jours"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Mot de passe (facultatif)</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="link-password-input" /></div>
          </div>
          <div className="flex items-center gap-2"><Switch checked={allowDl} onCheckedChange={setAllowDl} id="dl" data-testid="link-download-switch" /><Label htmlFor="dl" className="text-sm">Autoriser le téléchargement</Label></div>
          <Button onClick={create} data-testid="link-create-btn">Créer le lien</Button>
        </div>
      )}
      <div className="space-y-2" data-testid="file-links-list">
        {links.map((l) => {
          const expired = new Date(l.expires_at) < new Date();
          return (
            <div key={l.id} className="bg-white border rounded-lg p-3 flex items-center gap-3 text-sm">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-xs truncate">{publicLinkUrl(l.token)}</div>
                <div className="text-xs text-slate-500">{l.revoked ? "Révoqué" : expired ? "Expiré" : `Expire le ${fmtDate(l.expires_at, true)}`} · {l.access_count} accès {l.has_password && "· protégé"}</div>
              </div>
              {!l.revoked && !expired && <>
                <Button variant="ghost" size="icon" aria-label="Copier" onClick={() => { navigator.clipboard?.writeText(publicLinkUrl(l.token)); toast.success("Lien copié"); }} data-testid={`link-copy-${l.id}`}><Copy className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" aria-label="Révoquer" onClick={() => revoke(l.id)} data-testid={`link-revoke-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
              </>}
            </div>
          );
        })}
      </div>
      <PermissionsDialog open={permOpen} onOpenChange={setPermOpen} item={file} kind="files" onSaved={() => { reload(); onChanged(); }} />
    </div>
  );
}

export function FileDrawer({ fileId, onClose, onChanged }) {
  const [file, setFile] = useState(null);
  const [tab, setTab] = useState("preview");
  const [officeOpen, setOfficeOpen] = useState(false);
  const load = () => api.get(`/files/${fileId}`).then((r) => setFile(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
  useEffect(() => { setFile(null); setTab("preview"); if (fileId) load(); }, [fileId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (file?.ai_status !== "pending") return;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [file]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Sheet open={!!fileId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col bg-slate-50" data-testid="file-drawer">
        {!file ? <div className="flex-1 flex items-center justify-center"><SheetTitle className="sr-only">Chargement</SheetTitle><SheetDescription className="sr-only">Chargement du document</SheetDescription><Loader2 className="h-6 w-6 animate-spin" /></div> : (
          <>
            <div className="bg-white border-b p-5 pr-12 flex items-start gap-3">
              <FileIcon file={file} size="lg" />
              <div className="min-w-0 flex-1">
                <SheetTitle className="font-display text-base sm:text-lg font-bold truncate" data-testid="file-drawer-title">{file.name}</SheetTitle>
                <SheetDescription className="text-xs">{fmtBytes(file.size)} · v{file.version} · {fmtDate(file.updated_at, true)}</SheetDescription>
              </div>
              {APP_NAMES[fileKind(file)] && ["edit", "owner"].includes(file.access) && (
                <Button size="sm" onClick={() => setOfficeOpen(true)} data-testid="file-office-edit-btn"><PenLine className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Modifier dans {APP_NAMES[fileKind(file)]}</span></Button>)}
              {officeOpen && <OfficeEditDialog file={file} kind={fileKind(file)} open={officeOpen} onOpenChange={setOfficeOpen} onDone={() => { load(); onChanged(); }} />}
              <Button size="sm" variant="outline" onClick={() => downloadFile(file)} data-testid="file-download-btn"><Download className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Télécharger</span></Button>
            </div>
            <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
              <CompanySuggestion file={file} onDone={() => { load(); onChanged(); }} />
              <TabsList className="mx-5 mt-4 self-start flex-wrap h-auto">
                <TabsTrigger value="preview" data-testid="tab-preview">Aperçu</TabsTrigger>
                <TabsTrigger value="info" data-testid="tab-info">Infos & IA</TabsTrigger>
                <TabsTrigger value="versions" data-testid="tab-versions"><History className="h-3.5 w-3.5 mr-1" />Versions</TabsTrigger>
                <TabsTrigger value="comments" data-testid="tab-comments">Commentaires</TabsTrigger>
                <TabsTrigger value="share" data-testid="tab-share">Partage</TabsTrigger>
                <TabsTrigger value="approval" data-testid="tab-approval">Validation</TabsTrigger>
              </TabsList>
              <div className="flex-1 overflow-auto p-5">
                <TabsContent value="preview" className="mt-0"><FilePreview file={file} textPreview={file.text_preview} onDownload={() => downloadFile(file)} /></TabsContent>
                <TabsContent value="info" className="mt-0"><InfoTab file={file} reload={load} onChanged={onChanged} /></TabsContent>
                <TabsContent value="versions" className="mt-0"><VersionsTab file={file} reload={load} onChanged={onChanged} /></TabsContent>
                <TabsContent value="comments" className="mt-0"><CommentsTab file={file} /></TabsContent>
                <TabsContent value="share" className="mt-0"><ShareTab file={file} reload={load} onChanged={onChanged} /></TabsContent>
                <TabsContent value="approval" className="mt-0"><ApprovalTab file={file} onChanged={() => { load(); onChanged(); }} /></TabsContent>
              </div>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
